import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Building2, MapPin, User, Check, AlertCircle, Loader, Users, UserPlus, Trash2, ArrowRight } from 'lucide-react';
import { usersService, clientsService } from '../../services';
import { User as UserType, Client } from '../../types';
import './AddFacilityModal.css';

interface AddFacilityModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (facility: any) => Promise<string>;
  /** When opening from a client page, lock to this client (no client selector). */
  initialClientId?: string;
  initialClientName?: string;
}

interface StaffEntry {
  userId: string;
  userName: string;
  role: string;
}

interface FormData {
  name: string;
  type: string;
  address: string;
  description: string;
  clientId: string;
  clientName: string;
  managerId: string;
  managerName: string;
  additionalStaff: StaffEntry[];
  operationalSince: string;
  coordinates: { lat: number; lng: number } | null;
  jurisdiction?: string;
  facility_category?: string;
}

const AddFacilityModal: React.FC<AddFacilityModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  initialClientId,
  initialClientName,
}) => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [createdFacilityId, setCreatedFacilityId] = useState<string | null>(null);
  const [formData, setFormData] = useState<FormData>({
    name: '',
    type: '',
    address: '',
    description: '',
    clientId: '',
    clientName: '',
    managerId: '',
    managerName: '',
    additionalStaff: [],
    operationalSince: '',
    coordinates: null,
  });
  const [jurisdiction, setJurisdiction] = useState('');
  const [category, setCategory] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [users, setUsers] = useState<UserType[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [userError, setUserError] = useState<string | null>(null);
  const [clients, setClients] = useState<Client[]>([]);
  const [loadingClients, setLoadingClients] = useState(false);
  const [clientsError, setClientsError] = useState<string | null>(null);
  const [newManagerMode, setNewManagerMode] = useState(false);
  const [newManagerData, setNewManagerData] = useState({ name: '', email: '', role: 'facility_manager' as 'facility_manager' | 'client' });
  const [newManagerErrors, setNewManagerErrors] = useState<Record<string, string>>({});
  const [isCreatingManager, setIsCreatingManager] = useState(false);
  const [createManagerError, setCreateManagerError] = useState<string | null>(null);

  const totalSteps = 3;
  const isClientLocked = Boolean(initialClientId);

  const wasOpen = useRef(false);

  // Load users when modal opens; pre-fill and lock client when opening from a client page
  useEffect(() => {
    const opening = isOpen && !wasOpen.current;
    wasOpen.current = isOpen;
    if (opening) {
      loadUsers();
      if (!initialClientId) loadClients();
      setNewManagerMode(false);
      setNewManagerData({ name: '', email: '', role: 'facility_manager' });
      setNewManagerErrors({});
      setCreateManagerError(null);
      setCreatedFacilityId(null);
      setCurrentStep(1);
      setErrors({});
      if (initialClientId) {
        setFormData((prev) => ({
          ...prev,
          clientId: initialClientId,
          clientName: initialClientName || '',
        }));
      }
    }
  }, [isOpen, initialClientId, initialClientName]);

  const loadUsers = async () => {
    try {
      setLoadingUsers(true);
      setUserError(null);
      const usersData = await usersService.getUsers();
      setUsers(usersData);
    } catch (err) {
      setUserError(err instanceof Error ? err.message : 'Failed to load users');
      console.error('Failed to load users:', err);
    } finally {
      setLoadingUsers(false);
    }
  };

  const loadClients = async () => {
    try {
      setLoadingClients(true);
      setClientsError(null);
      const clientsData = await clientsService.getClients();
      setClients(clientsData);
    } catch (err) {
      setClientsError(err instanceof Error ? err.message : 'Failed to load clients');
      console.error('Failed to load clients:', err);
    } finally {
      setLoadingClients(false);
    }
  };


  if (!isOpen) return null;

  const validateStep = (step: number): boolean => {
    const newErrors: Record<string, string> = {};

    switch (step) {
      case 1:
        if (!formData.name.trim()) {
          newErrors.name = 'Facility name is required';
        }
        if (!category) {
          newErrors.category = 'Select a facility category';
        }
        if (!formData.address.trim()) {
          newErrors.address = 'Address is required';
        }
        break;
      case 2:
        if (!formData.clientId) {
          newErrors.clientId = 'Client must be selected';
        }
        if (!formData.managerId) {
          newErrors.managerId = 'Facility manager must be assigned';
        }
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    setCurrentStep(currentStep - 1);
    setErrors({});
  };

  const handleSubmit = async () => {
    if (!validateStep(currentStep)) {
      return;
    }

    setIsSubmitting(true);

    try {
      const facilityId = await onSuccess({...formData, type: category, jurisdiction, facility_category: category});
      setCreatedFacilityId(facilityId);
    } catch (error) {
      console.error('Error creating facility:', error);
      // Show error to user
      setErrors({ submit: 'Failed to create facility. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const updateFormData = (field: keyof FormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const selectClient = (client: Client) => {
    updateFormData('clientId', client.id);
    updateFormData('clientName', client.name);
    // Reset manager selection and staff when client changes
    updateFormData('managerId', '');
    updateFormData('managerName', '');
    updateFormData('additionalStaff', []);
    setNewManagerMode(false);
    setNewManagerData({ name: '', email: '', role: 'facility_manager' });
    setNewManagerErrors({});
    setCreateManagerError(null);
  };

  const selectManager = (manager: UserType) => {
    updateFormData('managerId', manager.id);
    updateFormData('managerName', manager.name);
  };


  const handleCreateManager = async () => {
    const errs: Record<string, string> = {};
    if (!newManagerData.name.trim()) errs.name = 'Name is required';
    if (!newManagerData.email.trim()) {
      errs.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newManagerData.email.trim())) {
      errs.email = 'Enter a valid email address';
    }
    if (Object.keys(errs).length > 0) {
      setNewManagerErrors(errs);
      return;
    }

    setIsCreatingManager(true);
    setCreateManagerError(null);
    try {
      const newUser = await usersService.createUser({
        name: newManagerData.name.trim(),
        email: newManagerData.email.trim(),
        role: newManagerData.role,
        client_id: formData.clientId,
      });
      setUsers(prev => [...prev, newUser]);
      selectManager(newUser);
      setNewManagerMode(false);
      setNewManagerData({ name: '', email: '', role: 'facility_manager' });
      setNewManagerErrors({});
    } catch (err) {
      setCreateManagerError(err instanceof Error ? err.message : 'Failed to create manager');
    } finally {
      setIsCreatingManager(false);
    }
  };

  const addAdditionalStaff = (user: UserType) => {
    // Don't add duplicates or the primary manager
    if (
      user.id === formData.managerId ||
      formData.additionalStaff.some((s) => s.userId === user.id)
    ) {
      return;
    }
    setFormData((prev) => ({
      ...prev,
      additionalStaff: [
        ...prev.additionalStaff,
        { userId: user.id, userName: user.name, role: '' },
      ],
    }));
  };

  const updateAdditionalStaffRole = (userId: string, role: string) => {
    setFormData((prev) => ({
      ...prev,
      additionalStaff: prev.additionalStaff.map((s) =>
        s.userId === userId ? { ...s, role } : s,
      ),
    }));
  };

  const removeAdditionalStaff = (userId: string) => {
    setFormData((prev) => ({
      ...prev,
      additionalStaff: prev.additionalStaff.filter((s) => s.userId !== userId),
    }));
  };

  const renderStepIndicator = () => (
    <div className="step-indicator">
      {Array.from({ length: totalSteps }, (_, i) => (
        <div key={i} className={`step ${i + 1 <= currentStep ? 'active' : ''}`}>
          <div className="step-number">
            {i + 1 < currentStep ? <Check size={16} /> : i + 1}
          </div>
          <div className="step-label">
            {i === 0 ? 'Basic Info' : i === 1 ? 'Management' : 'Review'}
          </div>
        </div>
      ))}
    </div>
  );

  const renderStep1 = () => (
    <div className="form-step">
      <h3>Basic Information</h3>
      <div className="form-group"><label className="form-label">Jurisdiction<select className="form-input" value={jurisdiction} onChange={e => setJurisdiction(e.target.value)}><option value="">Not specified</option>{['NSW','VIC','QLD','SA','WA','TAS','ACT','NT','NZ'].map(code => <option key={code}>{code}</option>)}</select></label></div>
      <p>Enter the fundamental details about this water facility</p>

      <div className="form-group">
        <label htmlFor="facilityName" className="form-label">
          Facility Name *
        </label>
        <input
          id="facilityName"
          type="text"
          className={`form-input ${errors.name ? 'error' : ''}`}
          placeholder="e.g., Downtown Water Treatment Plant"
          value={formData.name}
          onChange={(e) => updateFormData('name', e.target.value)}
        />
        {errors.name && <span className="error-message">{errors.name}</span>}
      </div>

      <div className="form-group" role="group" aria-label="Facility category">
        <label className="form-label">Facility category *</label>
        <div className="facility-type-grid">
          {Object.entries({
            healthcare: 'Stricter Legionella and warm-water rules',
            aged_care: 'Stricter Legionella and warm-water rules',
            childcare: 'Water safety for children and staff',
            hotel: 'Guest accommodation and shared water systems',
            commercial: 'Offices, retail and public buildings',
            industrial: 'Manufacturing and process water systems',
            residential: 'Homes and shared residential water systems',
            other: 'Other facility uses and water systems',
          }).map(([code, hint]) => (
            <button key={code} type="button" aria-pressed={category === code}
              className={`facility-type-card ${category === code ? 'selected' : ''}`}
              onClick={() => { setCategory(code); setErrors(prev => ({...prev, category: ''})); }}>
              <div className="type-content">
                <h4>{code.replace('_', ' ').replace(/^./, c => c.toUpperCase())}</h4>
                <p>{hint}</p>
              </div>
            </button>
          ))}
        </div>
        {errors.category && <span className="error-message">{errors.category}</span>}
      </div>

      <div className="form-group">
        <label htmlFor="address" className="form-label">
          <MapPin size={16} />
          Address *
        </label>
        <input
          id="address"
          type="text"
          className={`form-input ${errors.address ? 'error' : ''}`}
          placeholder="Full street address"
          value={formData.address}
          onChange={(e) => updateFormData('address', e.target.value)}
        />
        {errors.address && <span className="error-message">{errors.address}</span>}
      </div>

      <div className="form-group">
        <label htmlFor="description" className="form-label">
          Description
        </label>
        <textarea
          id="description"
          className="form-input"
          rows={3}
          placeholder="Brief description of the facility's purpose and capabilities"
          value={formData.description}
          onChange={(e) => updateFormData('description', e.target.value)}
        />
      </div>
    </div>
  );

  const filteredManagers =
    formData.clientId ?
      users.filter((u) => u.client_id === formData.clientId)
    : [];

  const renderStep2 = () => (
    <div className="form-step">
      <h3>Management & Operations</h3>
      <p>
        {isClientLocked
          ? `Assign a facility manager for ${formData.clientName || 'this client'}`
          : 'Select the client and assign a facility manager'}
      </p>

      {!isClientLocked && (
        <div className="form-group">
          <label className="form-label">
            <Users size={16} />
            Client (Organization) *
          </label>

          {loadingClients ? (
            <div className="loading-container">
              <Loader className="loading-spinner" size={20} />
              <p>Loading clients...</p>
            </div>
          ) : clientsError ? (
            <div className="error-container">
              <AlertCircle className="error-icon" size={20} />
              <p>{clientsError}</p>
              <button className="btn btn-secondary btn-sm" onClick={loadClients}>
                Retry
              </button>
            </div>
          ) : clients.length === 0 ? (
            <div className="empty-state">
              <p>No clients available. Please create a client first.</p>
            </div>
          ) : (
            <div className="manager-selection">
              {clients.map((client) => (
                <button
                  key={client.id}
                  type="button"
                  className={`manager-card ${formData.clientId === client.id ? 'selected' : ''}`}
                  onClick={() => selectClient(client)}
                >
                  <div className="manager-avatar">
                    {client.name.split(' ').map((n) => n[0]).join('').slice(0, 2)}
                  </div>
                  <div className="manager-info">
                    <div className="manager-name">{client.name}</div>
                    <span className="manager-role">Client Organization</span>
                  </div>
                </button>
              ))}
            </div>
          )}
          {errors.clientId && <span className="error-message">{errors.clientId}</span>}
        </div>
      )}

      {isClientLocked && formData.clientName && (
        <div className="form-group form-group-readonly">
          <label className="form-label">
            <Users size={16} />
            Client
          </label>
          <p className="readonly-value">{formData.clientName}</p>
        </div>
      )}

      <div className="form-group">
        <label className="form-label">
          <User size={16} />
          Facility Manager *
        </label>

        {loadingUsers ? (
          <div className="loading-container">
            <Loader className="loading-spinner" size={20} />
            <p>Loading users...</p>
          </div>
        ) : userError ? (
          <div className="error-container">
            <AlertCircle className="error-icon" size={20} />
            <p>{userError}</p>
            <button className="btn btn-secondary btn-sm" onClick={loadUsers}>
              Retry
            </button>
          </div>
        ) : !formData.clientId && !isClientLocked ? (
          <div className="empty-state">
            <p>Select a client first to see available managers.</p>
          </div>
        ) : (
          <>
            {filteredManagers.length > 0 && (
              <div className="manager-selection">
                {filteredManagers.map((user) => (
                  <button
                    key={user.id}
                    type="button"
                    className={`manager-card ${formData.managerId === user.id ? 'selected' : ''}`}
                    onClick={() => { selectManager(user); setNewManagerMode(false); }}
                  >
                    <div className="manager-avatar">
                      {user.name.split(' ').map((n) => n[0]).join('')}
                    </div>
                    <div className="manager-info">
                      <div className="manager-name">{user.name}</div>
                      <div className="manager-email">{user.email}</div>
                      <span className="manager-role">{user.role === 'facility_manager' ? 'Facility manager' : user.role === 'client' ? 'Client' : user.role}</span>
                    </div>
                  </button>
                ))}
              </div>
            )}

            {filteredManagers.length === 0 && !newManagerMode && (
              <div className="empty-state">
                <p>No managers found for {formData.clientName || 'this client'}.</p>
              </div>
            )}

            {!newManagerMode && (
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                style={{ marginTop: '8px' }}
                onClick={() => setNewManagerMode(true)}
              >
                <UserPlus size={14} />
                {filteredManagers.length === 0 ? 'Add New Staff Member' : 'Add new staff member'}
              </button>
            )}

            {newManagerMode && (
              <div className="new-manager-form" style={{ marginTop: '12px', padding: '12px', border: '1px solid var(--color-border)', borderRadius: '8px', background: 'var(--color-surface-secondary, #f8fafc)' }}>
                <p style={{ margin: '0 0 10px', fontWeight: 500, fontSize: '0.875rem' }}>
                  Add a new staff member for {formData.clientName || 'this client'}
                </p>
                <div className="form-group" style={{ marginBottom: '8px' }}>
                  <label className="form-label">Name *</label>
                  <input
                    type="text"
                    className={`form-input ${newManagerErrors.name ? 'error' : ''}`}
                    placeholder="Full name"
                    value={newManagerData.name}
                    onChange={(e) => { setNewManagerData(prev => ({ ...prev, name: e.target.value })); setNewManagerErrors(prev => ({ ...prev, name: '' })); }}
                  />
                  {newManagerErrors.name && <span className="error-message">{newManagerErrors.name}</span>}
                </div>
                <div className="form-group" style={{ marginBottom: '8px' }}>
                  <label className="form-label">Email *</label>
                  <input
                    type="email"
                    className={`form-input ${newManagerErrors.email ? 'error' : ''}`}
                    placeholder="email@example.com"
                    value={newManagerData.email}
                    onChange={(e) => { setNewManagerData(prev => ({ ...prev, email: e.target.value })); setNewManagerErrors(prev => ({ ...prev, email: '' })); }}
                  />
                  {newManagerErrors.email && <span className="error-message">{newManagerErrors.email}</span>}
                </div>
                <div className="form-group" style={{ marginBottom: '12px' }}>
                  <label htmlFor="newPersonRole" className="form-label">Role *</label>
                  <select
                    id="newPersonRole"
                    className="form-input"
                    value={newManagerData.role}
                    onChange={(e) => setNewManagerData(prev => ({ ...prev, role: e.target.value as 'facility_manager' | 'client' }))}
                  >
                    <option value="facility_manager">Facility Manager</option>
                    <option value="client">Client</option>
                  </select>
                </div>
                {createManagerError && (
                  <div className="error-container" style={{ marginBottom: '8px' }}>
                    <AlertCircle size={16} />
                    <span>{createManagerError}</span>
                  </div>
                )}
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <button
                    type="button"
                    className="btn btn-primary btn-sm"
                    onClick={handleCreateManager}
                    disabled={isCreatingManager}
                  >
                    {isCreatingManager ? (
                      <><Loader size={14} className="loading-spinner" /> Creating...</>
                    ) : (
                      'Create & Select'
                    )}
                  </button>
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm"
                    onClick={() => { setNewManagerMode(false); setNewManagerErrors({}); setCreateManagerError(null); }}
                    disabled={isCreatingManager}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </>
        )}
        {errors.managerId && <span className="error-message">{errors.managerId}</span>}
      </div>

      {/* Additional Staff Section */}
      {(formData.managerId || isClientLocked) && (
        <div className="form-group">
          <label className="form-label">
            <Users size={16} />
            Additional Staff
            <span style={{ fontWeight: 400, color: 'var(--color-text-secondary)', marginLeft: 6, fontSize: '0.8rem' }}>(optional)</span>
          </label>

          {formData.additionalStaff.length > 0 && (
            <div className="staff-assignments-list" style={{ marginBottom: '8px' }}>
              {formData.additionalStaff.map((staff) => (
                <div
                  key={staff.userId}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '8px',
                    border: '1px solid var(--color-border)',
                    borderRadius: '6px',
                    marginBottom: '6px',
                    background: 'var(--color-surface-secondary, #f8fafc)',
                  }}
                >
                  <div className="manager-avatar" style={{ width: 32, height: 32, fontSize: '0.75rem', flexShrink: 0 }}>
                    {staff.userName.split(' ').filter((n) => n).map((n) => n[0]).join('').slice(0, 2)}
                  </div>
                  <span style={{ fontWeight: 500, flex: '0 0 auto', minWidth: 0 }}>{staff.userName}</span>
                  <input
                    type="text"
                    className="form-input"
                    placeholder="Role (e.g., Deputy Manager)"
                    value={staff.role}
                    onChange={(e) => updateAdditionalStaffRole(staff.userId, e.target.value)}
                    style={{ flex: 1, minWidth: 0 }}
                  />
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm"
                    onClick={() => removeAdditionalStaff(staff.userId)}
                    title="Remove"
                    style={{ flexShrink: 0 }}
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* User picker for additional staff */}
          {filteredManagers.filter(
            (u) =>
              u.id !== formData.managerId &&
              !formData.additionalStaff.some((s) => s.userId === u.id),
          ).length > 0 && (
            <div className="manager-selection" style={{ maxHeight: '160px', overflowY: 'auto' }}>
              {filteredManagers
                .filter(
                  (u) =>
                    u.id !== formData.managerId &&
                    !formData.additionalStaff.some((s) => s.userId === u.id),
                )
                .map((user) => (
                  <button
                    key={user.id}
                    type="button"
                    className="manager-card"
                    onClick={() => addAdditionalStaff(user)}
                    title={`Add ${user.name} as additional staff`}
                  >
                    <div className="manager-avatar">
                      {user.name.split(' ').filter((n) => n).map((n) => n[0]).join('')}
                    </div>
                    <div className="manager-info">
                      <div className="manager-name">{user.name}</div>
                      <div className="manager-email">{user.email}</div>
                      <span className="manager-role">{user.role === 'facility_manager' ? 'Facility manager' : user.role === 'client' ? 'Client' : user.role}</span>
                    </div>
                    <UserPlus size={14} style={{ marginLeft: 'auto', flexShrink: 0, opacity: 0.6 }} />
                  </button>
                ))}
            </div>
          )}
        </div>
      )}

      <div className="form-group">
        <label htmlFor="operationalSince" className="form-label">
          Operational Since
        </label>
        <input
          id="operationalSince"
          type="date"
          className="form-input"
          value={formData.operationalSince}
          onChange={(e) => updateFormData('operationalSince', e.target.value)}
        />
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className="form-step">
      <h3>Review & Create</h3>
      <p>Review the information and create your facility</p>

      {errors.submit && (
        <div className="error-alert">
          <AlertCircle size={20} />
          <span>{errors.submit}</span>
        </div>
      )}

      <div className="review-section">
        <div className="review-item">
          <h4>Basic Information</h4>
          <div className="review-content">
            <p><strong>Name:</strong> {formData.name}</p>
            <p><strong>Jurisdiction:</strong> {jurisdiction || 'Not specified'}</p><p><strong>Facility category:</strong> {category ? category.replace(/_/g, ' ').replace(/^./, c => c.toUpperCase()) : 'Not specified'}</p>
            <p><strong>Address:</strong> {formData.address}</p>
            {formData.description && <p><strong>Description:</strong> {formData.description}</p>}
          </div>
        </div>

        <div className="review-item">
          <h4>Management</h4>
          <div className="review-content">
            <p><strong>Client:</strong> {formData.clientName}</p>
            <p><strong>Manager:</strong> {formData.managerName}</p>
            {formData.additionalStaff.length > 0 && (
              <div>
                <strong>Additional Staff:</strong>
                <ul style={{ margin: '4px 0 0 16px', padding: 0 }}>
                  {formData.additionalStaff.map((s) => (
                    <li key={s.userId}>
                      {s.userName}{s.role ? ` — ${s.role}` : ''}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {formData.operationalSince && <p><strong>Operational Since:</strong> {formData.operationalSince}</p>}
          </div>
        </div>

      </div>
    </div>
  );

  return (
    <div className="modal-overlay">
      <div className="modal-content add-facility-modal">
        <div className="modal-header">
          <h2>
            <Building2 size={24} />
            Add New Facility
          </h2>
          <button className="close-button" onClick={onClose}>
            <X size={24} />
          </button>
        </div>

        {!createdFacilityId && renderStepIndicator()}

        <div className="modal-body">
          {createdFacilityId ? (
            <div className="form-step facility-created-success">
              <div className="success-icon">
                <Check size={48} />
              </div>
              <h3>{formData.name} created!</h3>
              <p>Your facility has been set up and assigned to {formData.managerName}.</p>
              <div className="next-steps">
                <h4>What happens next?</h4>
                <ul>
                  <li>Add assets to define what needs to be inspected</li>
                  <li>Set up checkpoints on each asset</li>
                  <li>Start your first inspection</li>
                </ul>
              </div>
              <button
                type="button"
                className="btn btn-primary btn-go-to-assets"
                onClick={() => {
                  onClose();
                  navigate(`/facilities/${createdFacilityId}`);
                }}
              >
                Open facility
                <ArrowRight size={16} />
              </button>
            </div>
          ) : (
            <>
              {currentStep === 1 && renderStep1()}
              {currentStep === 2 && renderStep2()}
              {currentStep === 3 && renderStep3()}
            </>
          )}
        </div>

        <div className="modal-footer">
          <div className="footer-actions">
            {createdFacilityId ? (
              <button type="button" className="btn btn-ghost" onClick={onClose}>
                Close
              </button>
            ) : (
              <>
                <button
                  type="button"
                  className="btn btn-ghost"
                  onClick={onClose}
                >
                  Cancel
                </button>

                <div className="navigation-buttons">
                  {currentStep > 1 && (
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={handleBack}
                    >
                      Back
                    </button>
                  )}

                  {currentStep < totalSteps ? (
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={handleNext}
                    >
                      Next
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={handleSubmit}
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? 'Creating Facility...' : 'Create Facility'}
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddFacilityModal;
